import { LoadingButton } from "@mui/lab";
import { UserReport, useUserReport } from "./reportUtils";
import { useTranslation } from "react-i18next";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridToolbar
} from "@mui/x-data-grid";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { TableLoadingOverlay } from "../../common-ui/TableLoadingOverlay";
import { useEffect, useState } from "react";
import { ProgramRoleSelectFields } from "../../app/constants";

export const ReportLoadingOverlay = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: "white"
      }}
    >
      <CircularProgress />
      <Typography>{t("Please wait and do not refresh this page.")}</Typography>
      <Typography>{t("Report generation can take a while...")}</Typography>
    </Box>
  );
};

interface FrontendUserReportProps {
  programId?: string;
  columns: GridColDef<UserReport>[];
  handleRowClick: GridEventListener<"rowClick"> | undefined;
  roleFilter: string;
}

declare module "@mui/x-data-grid" {
  interface LoadingOverlayPropsOverrides {
    message: string;
  }
}

export const FrontendUserReports = ({
  programId,
  columns,
  handleRowClick,
  roleFilter
}: FrontendUserReportProps) => {
  const {
    status: fetchReportStatus,
    error: fetchReportError,
    report: frontendReport,
    generateUserReport
  } = useUserReport();
  const { t } = useTranslation();

  const [report, setReport] = useState<UserReport[]>([]);

  const prepareReport = (frontendReport: UserReport[], roleFilter: string) => {
    const newReport: UserReport[] = [];
    for (const index in frontendReport) {
      const item = frontendReport[index];
      const programRoleValue = item.programRole;
      const programRoleString = ProgramRoleSelectFields.find(
        (roleSelectField) => roleSelectField.value === programRoleValue
      )?.label;
      if (roleFilter) {
        if (item.programRole !== roleFilter) {
          continue;
        }
      }
      item.programRoleString = programRoleString || "N/A";
      newReport.push(item);
    }
    setReport(newReport);
  };

  useEffect(() => {
    prepareReport(frontendReport, roleFilter);
  }, [frontendReport, roleFilter]);

  if (fetchReportStatus === "failed") {
    return <Alert severity="error">{fetchReportError}</Alert>;
  }

  return (
    <>
      <div>
        <LoadingButton
          variant="contained"
          disabled={!programId}
          loading={fetchReportStatus === "loading"}
          onClick={() => {
            if (programId) {
              generateUserReport(programId);
            }
          }}
        >
          {t("Generate Report")}
        </LoadingButton>
      </div>
      <DataGrid
        aria-label="reports-table"
        rows={report}
        columns={columns}
        loading={fetchReportStatus === "loading"}
        slots={{
          loadingOverlay: TableLoadingOverlay,
          toolbar: GridToolbar
        }}
        slotProps={{
          loadingOverlay: {
            message:
              "Please wait and do not refresh this page. Report generation can take a while..."
          }
        }}
        onRowClick={handleRowClick}
      />
    </>
  );
};
